// client/src/redux/Slices/DestinationsSlice.ts
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import destinationsApiInstance from "../../api/DestinationsApi";
import { ILiveStream, IStreamingPlatform } from "../../types/models";
import { IStreamingPlatformKey } from "../../types/interfaces";
import { notification } from "antd";
import { dispatchErrorNotification } from "../../helpers/errorDispatch";
import { destinationChangeNeedsUpdate } from "../../helpers/destinationApply";
import i18n from "../../i18n";

interface DestinationsState {
  destinations: IStreamingPlatform[];
  loading: boolean;
  streamId: string | null;
  requestId: string | null;
  mutationRequestIds: Record<string, string[]>;
}

const initialState: DestinationsState = {
  destinations: [],
  loading: false,
  streamId: null,
  requestId: null,
  mutationRequestIds: {},
};

export const destinationsSlice = createSlice({
  name: "destinations",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setDestinations(state, action: PayloadAction<IStreamingPlatform[]>) {
      state.destinations = action.payload;
    },
    beginDestinationsRequest(
      state,
      action: PayloadAction<{ streamId: string; requestId: string }>,
    ) {
      if (state.streamId !== action.payload.streamId) state.destinations = [];
      state.streamId = action.payload.streamId;
      state.requestId = action.payload.requestId;
      state.loading = true;
    },
    beginDestinationMutation(
      state,
      action: PayloadAction<{ streamId: string; requestId: string }>,
    ) {
      if (state.streamId !== action.payload.streamId) {
        state.streamId = action.payload.streamId;
        state.destinations = [];
        state.requestId = null;
      }
      state.mutationRequestIds[action.payload.streamId] = [
        ...(state.mutationRequestIds[action.payload.streamId] || []),
        action.payload.requestId,
      ];
      state.loading = true;
    },
    finishDestinationMutation(
      state,
      action: PayloadAction<{ streamId: string; requestId: string }>,
    ) {
      state.mutationRequestIds[action.payload.streamId] = (
        state.mutationRequestIds[action.payload.streamId] || []
      ).filter((id) => id !== action.payload.requestId);
      if (state.streamId === action.payload.streamId)
        state.loading =
          Boolean(state.requestId) ||
          state.mutationRequestIds[action.payload.streamId].length > 0;
    },
    completeDestinationsRequest(
      state,
      action: PayloadAction<{
        streamId: string;
        requestId: string;
        destinations: IStreamingPlatform[];
      }>,
    ) {
      if (
        state.streamId === action.payload.streamId &&
        state.requestId === action.payload.requestId
      )
        state.destinations = action.payload.destinations;
    },
    finishDestinationsRequest(
      state,
      action: PayloadAction<{ streamId: string; requestId: string }>,
    ) {
      if (
        state.streamId === action.payload.streamId &&
        state.requestId === action.payload.requestId
      ) {
        state.loading =
          (state.mutationRequestIds[action.payload.streamId] || []).length > 0;
        state.requestId = null;
      }
    },
    removeDestination(
      state,
      action: PayloadAction<{ streamId: string; id: string }>,
    ) {
      if (state.streamId && state.streamId !== action.payload.streamId) return;
      state.streamId = action.payload.streamId;
      state.destinations = state.destinations.filter(
        (d) => d.id !== action.payload.id,
      );
    },
    upsertDestination(
      state,
      action: PayloadAction<{
        streamId: string;
        destination: IStreamingPlatform;
      }>,
    ) {
      if (state.streamId && state.streamId !== action.payload.streamId) return;
      state.streamId = action.payload.streamId;
      const i = state.destinations.findIndex(
        (d) => d.id === action.payload.destination.id,
      );
      if (i === -1)
        state.destinations = [action.payload.destination, ...state.destinations];
      else state.destinations[i] = action.payload.destination;
    },
  },
});

export const {
  setLoading,
  setDestinations,
  beginDestinationsRequest,
  beginDestinationMutation,
  finishDestinationMutation,
  completeDestinationsRequest,
  finishDestinationsRequest,
  removeDestination,
  upsertDestination,
} =
  destinationsSlice.actions;

type ApplyHintState = {
  liveStream?: { liveStreamById?: ILiveStream | null };
  destinations?: { destinations?: IStreamingPlatform[] };
};

/** B15: saved; the running worker keeps the old destinations until "Update Live Stream". */
function hintApplyOnUpdate(
  state: ApplyHintState,
  streamId: string,
  destinationEnabled: boolean,
) {
  if (
    destinationChangeNeedsUpdate(
      state.liveStream?.liveStreamById,
      streamId,
      destinationEnabled,
    )
  )
    notification.info({
      message: i18n.t("liveStream:streamKey.notifications.applyOnUpdate"),
    });
}

export const getDestinations = createAsyncThunk(
  "destinations/get",
  async (streamId: string, { dispatch, requestId }) => {
    dispatch(beginDestinationsRequest({ streamId, requestId }));
    try {
      const res = await destinationsApiInstance.list(streamId);
      dispatch(
        completeDestinationsRequest({
          streamId,
          requestId,
          destinations: res.data,
        }),
      );
    } catch (err: any) {
      dispatchErrorNotification(err);
    } finally {
      dispatch(finishDestinationsRequest({ streamId, requestId }));
    }
  },
);

export const deleteDestination = createAsyncThunk(
  "destinations/delete",
  async ({ streamId, id }: { streamId: string; id: string }, { dispatch, requestId, getState }) => {
    const wasEnabled = !!(getState() as ApplyHintState).destinations?.destinations?.find(
      (d) => d.id === id,
    )?.enabled;
    dispatch(beginDestinationMutation({ streamId, requestId }));
    try {
      await destinationsApiInstance.remove(streamId, id);
      dispatch(removeDestination({ streamId, id }));
      notification.success({ message: i18n.t("liveStream:streamKey.notifications.deleted") });
      hintApplyOnUpdate(getState() as ApplyHintState, streamId, wasEnabled);
    } catch (err: any) {
      dispatchErrorNotification(err);
    } finally {
      dispatch(finishDestinationMutation({ streamId, requestId }));
    }
  },
);

export const updateDestination = createAsyncThunk(
  "destinations/update",
  async ({ streamId, platform }: { streamId: string; platform: IStreamingPlatformKey }, { dispatch, requestId, getState }) => {
    dispatch(beginDestinationMutation({ streamId, requestId }));
    try {
      const { id, ...body } = platform;
      const res = await destinationsApiInstance.update(streamId, id, body);
      dispatch(upsertDestination({ streamId, destination: res.data }));
      notification.success({ message: i18n.t("liveStream:streamKey.notifications.updated") });
      hintApplyOnUpdate(getState() as ApplyHintState, streamId, !!res.data?.enabled);
    } catch (err: any) {
      dispatchErrorNotification(err);
    } finally {
      dispatch(finishDestinationMutation({ streamId, requestId }));
    }
  },
);

export const addDestination = createAsyncThunk(
  "destinations/add",
  async ({ streamId, platform }: { streamId: string; platform: Omit<IStreamingPlatformKey, "id"> }, { dispatch, requestId }) => {
    dispatch(beginDestinationMutation({ streamId, requestId }));
    try {
      const res = await destinationsApiInstance.create(streamId, platform);
      dispatch(upsertDestination({ streamId, destination: res.data }));
      notification.success({ message: i18n.t("liveStream:streamKey.notifications.added") });
      return res.data;
    } catch (err: any) {
      dispatchErrorNotification(err);
      throw err;
    } finally {
      dispatch(finishDestinationMutation({ streamId, requestId }));
    }
  },
);
